import { Fragment, useState } from 'react';
import {
  useDecideMtoPassportMutation,
  useLazyExportMtoPassportQuery,
} from '@shared/api';
import type { MtoPassport } from '@entities/mto';
import {
  apiErrorMessage,
  decisionLabel,
  decisionTone,
  labelIndicator,
  matchStatusLabel,
  matchStatusTone,
  passportStatusLabel,
  passportStatusTone,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  IconChevronDown,
  IconChevronRight,
  IconDownload,
} from '@shared/ui/icons';
import {
  DownloadButton,
  ExpandButton,
  IndicatorCell,
  IndicatorLabel,
  IndicatorList,
  IndicatorValue,
  Muted,
  RowActions,
  Table,
  TableScroll,
} from '../styles';

/** Одна строка паспорта в таблице + раскрывашка показателей качества. */
export function PassportRow({
  passport,
  runId,
  canWrite,
  onError,
}: {
  passport: MtoPassport;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [decide, { isLoading: deciding }] = useDecideMtoPassportMutation();
  const [exportDoc, { isFetching: downloading }] = useLazyExportMtoPassportQuery();

  const indicators = Object.entries(passport.indicators);
  const decided = Boolean(passport.decision);

  async function handleDecide(decision: 'accepted' | 'rejected') {
    onError('');
    try {
      await decide({ id: passport.id, runId, decision }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось сохранить решение по паспорту.');
      // eslint-disable-next-line no-console
      console.error('decideMtoPassport failed', err);
    }
  }

  async function handleDownload() {
    onError('');
    try {
      const blob = await exportDoc(passport.id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passport_${passport.id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось скачать паспорт качества.');
      // eslint-disable-next-line no-console
      console.error('exportMtoPassport failed', err);
    }
  }

  return (
    <>
      <tr>
        <td>
          {passport.batch_no ?? '—'}
          {indicators.length > 0 && (
            <>
              <br />
              <ExpandButton
                type="button"
                $open={open}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                Показатели ({indicators.length})
              </ExpandButton>
            </>
          )}
        </td>
        <td>{passport.quarry ?? '—'}</td>
        <td>{passport.buyer ?? '—'}</td>
        <td>{passport.passport_no ?? '—'}</td>
        <td>
          <Badge $tone={matchStatusTone(passport.match_status)}>
            {matchStatusLabel(passport.match_status)}
          </Badge>
        </td>
        <td>
          <Badge $tone={passportStatusTone(passport.status)}>
            {passportStatusLabel(passport.status)}
          </Badge>
        </td>
        <td>
          <RowActions>
            {passport.has_document && (
              <DownloadButton type="button" onClick={handleDownload} disabled={downloading}>
                <IconDownload size={14} />
                {downloading ? 'Готовлю…' : 'Word'}
              </DownloadButton>
            )}
            {!decided ? (
              <>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={!canWrite || deciding}
                  title={!canWrite ? 'Только просмотр' : undefined}
                  onClick={() => handleDecide('accepted')}
                >
                  Принять
                </Button>
                <Button
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  disabled={!canWrite || deciding}
                  title={!canWrite ? 'Только просмотр' : undefined}
                  onClick={() => handleDecide('rejected')}
                >
                  Отклонить
                </Button>
              </>
            ) : (
              <Badge $tone={decisionTone(passport.decision ?? '')}>
                {decisionLabel(passport.decision ?? '')}
              </Badge>
            )}
          </RowActions>
        </td>
      </tr>
      {open && indicators.length > 0 && (
        <tr>
          <IndicatorCell colSpan={7}>
            <IndicatorList>
              {indicators.map(([code, value]) => (
                <Fragment key={code}>
                  <IndicatorLabel>{labelIndicator(code)}</IndicatorLabel>
                  <IndicatorValue>{value}</IndicatorValue>
                </Fragment>
              ))}
            </IndicatorList>
          </IndicatorCell>
        </tr>
      )}
    </>
  );
}

/** Блок «Паспорта» — таблица собранных паспортов качества с HITL и скачиванием. */
export function PassportsSection({
  passports,
  runId,
  canWrite,
  onError,
}: {
  passports: MtoPassport[];
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  if (passports.length === 0) {
    return <Muted>Паспорта появятся после завершения обработки.</Muted>;
  }
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th scope="col">Партия</th>
            <th scope="col">Карьер</th>
            <th scope="col">Покупатель</th>
            <th scope="col">№ паспорта</th>
            <th scope="col">Сопоставление</th>
            <th scope="col">Статус</th>
            <th scope="col" aria-label="Действия" />
          </tr>
        </thead>
        <tbody>
          {passports.map((p) => (
            <PassportRow
              key={p.id}
              passport={p}
              runId={runId}
              canWrite={canWrite}
              onError={onError}
            />
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
